import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = `trk:inbox:debugMode`
const CHANGE_EVENT = `trk:inbox:debugMode:change`

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === `true`
    // oxlint-disable-next-line local/require-catch-logging -- private-mode localStorage access can throw; default off
  } catch {
    return false
  }
}

/**
 * Super-user inbox debug mode. Persisted in `localStorage` so it survives reloads
 * and thread navigation — it is a "mode", not per-thread state.
 *
 * A same-tab custom event keeps every consumer of the hook in sync when the value
 * changes. The native `storage` event only fires in *other* tabs, so without this
 * the right-panel toggle and the message list would diverge until an unrelated
 * re-render happened to pick up the new value.
 *
 * The hook itself is not super-user aware. Callers gate the toggle UI and the
 * trace fetch on `isSuperUser`, so regular users never see the switch or query.
 */
export function useInboxDebugMode(): { debugMode: boolean; setDebugMode: (value: boolean) => void } {
  const [debugModeState, setDebugModeState] = useState<boolean>(readStored)

  useEffect(() => {
    const sync = () => setDebugModeState(readStored())
    window.addEventListener(CHANGE_EVENT, sync)
    window.addEventListener(`storage`, sync)
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync)
      window.removeEventListener(`storage`, sync)
    }
  }, [])

  const setDebugMode = useCallback((value: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, value ? `true` : `false`)
      // oxlint-disable-next-line local/require-catch-logging -- ignore write failures (private mode); in-memory state still updates
    } catch {
      // no-op
    }
    setDebugModeState(value)
    window.dispatchEvent(new Event(CHANGE_EVENT))
  }, [])

  return { debugMode: debugModeState, setDebugMode }
}
